import { Context, Effect, Layer } from 'effect'

export type PageSurfaceService = {
  /**
   * Presents a borrowed bitmap on an ephemeral browser surface.
   *
   * The caller retains ownership and may present the same bitmap again when
   * React replaces a canvas. A surface that disappeared before this Effect
   * runs is an ordinary view-lifecycle race and is therefore a no-op.
   */
  readonly present: (surfaceId: string, bitmap: ImageBitmap) => Effect.Effect<void>
}

export class PageSurface extends Context.Service<PageSurface, PageSurfaceService>()(
  'PageSurface'
) {}

/**
 * Physical browser boundary for page pixels.
 *
 * The document renderer owns rasterization, retention, and `ImageBitmap.close`.
 * This layer only resolves the current DOM surface and copies the borrowed
 * bitmap into its visible canvas so browser replay and screenshot tooling can
 * observe the final paint.
 */
export const BrowserPageSurfaceLive = Layer.succeed(PageSurface, {
  present: (surfaceId, bitmap) =>
    Effect.gen(function* () {
      if (typeof document == 'undefined') {
        return
      }

      const element = document.getElementById(surfaceId)
      if (!element) {
        return
      }
      if (!(element instanceof HTMLCanvasElement)) {
        return yield* Effect.die(new Error(`Page surface ${surfaceId} is not an HTMLCanvasElement`))
      }

      const context2d = element.getContext('2d')
      if (!context2d) {
        return yield* Effect.die(
          new Error(`Page surface ${surfaceId} does not support a 2D rendering context`)
        )
      }

      const width = bitmap.width
      const height = bitmap.height

      yield* Effect.sync(() => {
        if (element.width != width) {
          element.width = width
        }
        if (element.height != height) {
          element.height = height
        }

        context2d.clearRect(0, 0, width, height)
        context2d.drawImage(bitmap, 0, 0, width, height)
      })
    })
})
